import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Shield, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Small delay so it doesn't flash on load
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleNecessaryOnly = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[90] p-4 md:p-6"
        >
          <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl border border-border/50 bg-card/95 shadow-2xl backdrop-blur-xl">
            <div className="p-5 md:p-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
                {/* Icon & Text */}
                <div className="flex flex-1 gap-4">
                  <div className="hidden sm:flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <Cookie className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="mb-1.5 font-display text-base font-bold text-foreground">
                      {language === 'PL' ? 'Ta strona używa plików cookies' : 'This website uses cookies'}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {language === 'PL'
                        ? 'Używamy plików cookies, aby zapewnić najlepsze doświadczenia na naszej stronie. Pliki cookies pomagają nam analizować ruch i dostosowywać treści. '
                        : 'We use cookies to ensure the best experience on our website. Cookies help us analyze traffic and customize content. '}
                      <Link to="/privacy-policy" className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                        {language === 'PL' ? 'Polityka prywatności' : 'Privacy policy'}
                      </Link>
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center md:flex-shrink-0">
                  <button
                    onClick={handleNecessaryOnly}
                    className="rounded-xl border border-border/60 px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
                  >
                    {language === 'PL' ? 'Tylko niezbędne' : 'Necessary only'}
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-lg transition-all hover:bg-primary/90 hover:shadow-xl"
                  >
                    {language === 'PL' ? 'Akceptuję wszystkie' : 'Accept all'}
                  </button>
                </div>
              </div>

              {/* Bottom info */}
              <div className="mt-3 flex items-center gap-1.5 text-[10px] text-muted-foreground/50">
                <Shield className="h-3 w-3" />
                <span>{language === 'PL' ? 'Twoje dane są bezpieczne zgodnie z RODO' : 'Your data is safe under GDPR'}</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
